import { SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import NumberInput from "../../../common/NumberInput";
import {
  COLORS,
  FakeLink,
  FlashPCBTable,
  Input,
  InputLabel,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../../constants/Styles";
import {
  Board,
  CURRENCY,
  Quote,
  QuoteItem,
  QuoteState,
} from "../../../../reducers/uploadedBoard";
import { FlashPCBSettings } from "../../../../utils/settings";
import { QuoteToolContainer, QuoteToolTitle } from "../QuoteStyles";
import Button from "../../../common/Button";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import {
  ButtonContainer,
  SetQuoteContainer,
} from "../Breakdown/BreakdownStyles";
import { DateInputContainer } from "../../../common/FlashPCBDatePicker/FlashPCBDatePickerStyles";
import {
  formatCurrency,
  todayInPhillyToDateInputString,
  twentyFourHourToTwelveHour,
} from "../../../../utils/valueFormatting";
import { useAppSelector } from "../../../../utils/hooks/store";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import { useCustomConfirm } from "../../../../utils/hooks/alerts";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import {
  ActionAndSummarySection,
  InputContainer,
  SelectInputContainer,
  TableContainer,
  TableTitle,
} from "./UpdateCustomQuoteStyles";
import LineItem from "../../../common/LineItem";
import {
  FlashPCBCreatableSelect,
  OptionType,
} from "../../../common/FlashPCBSelect";
import {
  QuoteItemUpdate,
  useUpdateQuoteItem,
} from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/customQuote";
import QuoteBreakdown from "../Breakdown";

const DEFAULT_EXPIRATION = 7;
const ITEM_UPDATE_DEBOUNCE_MS = 500;
const NEW_ITEM_NAME = "New item";

enum AdditionalQuoteItems {
  STAKING = "Staking",
  LABELS = "Labels",
  CONFORMAL_COATING = "Conformal coating",
  FIRST_ARTICLE = "First article",
  PART_TARIFF = "Part tariff",
}

interface EditableQuoteRow {
  item: QuoteItem;
  // null for top-level items such as Tariffs
  parent: QuoteItem | null;
}

export function CustomQuoteTableRow({
  quoteItem,
  currency,
  parentItem,
  isPendingRow,
  onDiscardPendingRow,
}: {
  quoteItem: QuoteItem;
  currency: CURRENCY;
  parentItem: QuoteItem | null;
  // A row added locally that the server has not been told about yet
  isPendingRow: boolean;
  onDiscardPendingRow: (itemId: string) => void;
}) {
  const additionalQuoteItems = Object.values(AdditionalQuoteItems) as string[];
  const [amount, setAmount] = useState<number>(+quoteItem.amount.toFixed(3));
  const [basePrice, setBasePrice] = useState<number>(
    +quoteItem.base_price.toFixed(3),
  );
  const [markUp, setMarkUp] = useState<number>(quoteItem.markup);
  const [name, setName] = useState<string>(quoteItem.name);
  // Once a pending row has been sent, picking another name renames it
  // instead of adding a second item with the same id.
  const hasBeenAdded = useRef(!isPendingRow);
  const { updateQuoteItem, removeQuoteItem, addQuoteItem } =
    useUpdateQuoteItem();

  // One event per pause in typing rather than one per keystroke. Each event
  // is an independent Celery task, so per-keystroke events could be applied
  // out of order.
  const sendUpdate = useDebouncedCallback((update: QuoteItemUpdate) => {
    updateQuoteItem(update);
  }, ITEM_UPDATE_DEBOUNCE_MS);

  useEffect(() => {
    return () => {
      sendUpdate.flush();
    };
  }, [sendUpdate]);

  const buildUpdate = (
    overrides: Partial<QuoteItemUpdate>,
  ): QuoteItemUpdate => {
    return {
      ...quoteItem,
      name,
      base_price: basePrice,
      amount,
      markup: markUp,
      parent: parentItem,
      ...overrides,
    };
  };

  const updateMarkup = (newMarkup: number) => {
    setMarkUp(newMarkup);
    const newAmount = basePrice * newMarkup;
    setAmount(newAmount);
    if (hasBeenAdded.current) {
      sendUpdate(buildUpdate({ amount: newAmount, markup: newMarkup }));
    }
  };

  const updateAmount = (newPrice: number) => {
    setBasePrice(newPrice);
    const newAmount = newPrice * markUp;
    setAmount(newAmount);
    if (hasBeenAdded.current) {
      sendUpdate(buildUpdate({ base_price: newPrice, amount: newAmount }));
    }
  };

  const updateName = (newName: OptionType | null) => {
    if (!newName) {
      return;
    }
    setName(newName.value);
    const update = buildUpdate({ name: newName.value });
    if (hasBeenAdded.current) {
      sendUpdate.cancel();
      updateQuoteItem(update);
    } else {
      hasBeenAdded.current = true;
      addQuoteItem(update);
    }
  };

  const removeItem = () => {
    if (
      additionalQuoteItems.includes(quoteItem.name) ||
      quoteItem.manually_added
    ) {
      sendUpdate.cancel();
      removeQuoteItem(buildUpdate({}));
      onDiscardPendingRow(quoteItem.id);
    } else {
      updateMarkup(0);
    }
  };

  return (
    <TableRow>
      <TableCell>
        {quoteItem.name === NEW_ITEM_NAME ? (
          <SelectInputContainer>
            <FlashPCBCreatableSelect
              options={additionalQuoteItems.map((itemName) => ({
                value: itemName,
                label: itemName,
              }))}
              onChange={updateName}
            />
          </SelectInputContainer>
        ) : (
          name
        )}
      </TableCell>
      <TableCell alignRight>
        {formatCurrency(quoteItem.reference_amount, currency)}
      </TableCell>
      <TableCell>
        {!quoteItem.children.length ? (
          <InputContainer>
            <NumberInput
              name={quoteItem.name}
              min={0}
              onChange={(newValue) => updateAmount(newValue || 0)}
              value={basePrice}
            />
          </InputContainer>
        ) : (
          formatCurrency(quoteItem.base_price, currency)
        )}
      </TableCell>
      <TableCell>
        <InputContainer>
          <NumberInput
            name={quoteItem.name}
            min={0}
            onChange={(newValue) => updateMarkup(newValue || 0)}
            value={markUp}
          />
        </InputContainer>
      </TableCell>
      <TableCell alignRight>{formatCurrency(amount, currency)}</TableCell>
      <TableCell>
        <FakeLink onClick={removeItem}>Remove</FakeLink>
      </TableCell>
    </TableRow>
  );
}

const CustomQuoteTableHeader = () => (
  <TableHeaderRow>
    <TableHeaderCell>Item</TableHeaderCell>
    <TableHeaderCell alignRight>Estimated amt</TableHeaderCell>
    <TableHeaderCell>Actual amt</TableHeaderCell>
    <TableHeaderCell>Markup</TableHeaderCell>
    <TableHeaderCell alignRight>Quoted amt</TableHeaderCell>
    <TableHeaderCell>Remove item</TableHeaderCell>
  </TableHeaderRow>
);

function CustomQuoteTable({
  title,
  rows,
  currency,
  newItemParent,
}: {
  title: string;
  rows: EditableQuoteRow[];
  currency: CURRENCY;
  newItemParent: QuoteItem;
}) {
  const { updatingItem } = useUpdateQuoteItem();
  // Rows render from the store so server-side removals and edits show up.
  // Locally added rows stay until the server returns them.
  const [pendingRows, setPendingRows] = useState<QuoteItem[]>([]);
  const serverItemIds = new Set(rows.map((row) => row.item.id));
  const visiblePendingRows = pendingRows.filter(
    (item) => !serverItemIds.has(item.id),
  );
  const addBlankTableRow = () => {
    const newItem: QuoteItem = {
      id: crypto.randomUUID(),
      name: NEW_ITEM_NAME,
      reference_amount: 0,
      base_price: 0,
      amount: 0,
      markup: 1,
      children: [],
      description: "",
      manually_added: true,
    };
    setPendingRows((prev) => [...prev, newItem]);
  };

  const discardPendingRow = (itemId: string) => {
    setPendingRows((prev) => prev.filter((item) => item.id !== itemId));
  };

  const subTotal = rows.reduce((total, row) => {
    return total + row.item.amount;
  }, 0);

  return (
    <TableContainer>
      <TableTitle>{title}</TableTitle>
      <FlashPCBTable>
        <CustomQuoteTableHeader />
        <tbody>
          {rows.map((row) => (
            <CustomQuoteTableRow
              key={row.item.id}
              quoteItem={row.item}
              currency={currency}
              parentItem={row.parent}
              isPendingRow={false}
              onDiscardPendingRow={discardPendingRow}
            />
          ))}
          {visiblePendingRows.map((item) => (
            <CustomQuoteTableRow
              key={item.id}
              quoteItem={item}
              currency={currency}
              parentItem={newItemParent}
              isPendingRow
              onDiscardPendingRow={discardPendingRow}
            />
          ))}
        </tbody>
      </FlashPCBTable>
      <ActionAndSummarySection>
        <Button
          label={"Add item"}
          onClick={() => addBlankTableRow()}
          isSmallSize
          disabled={updatingItem}
        />
        <LineItem
          leftText={`${title} subtotal`}
          rightText={formatCurrency(subTotal, currency)}
          isUpdating={updatingItem}
        />
      </ActionAndSummarySection>
    </TableContainer>
  );
}

const EditableLineItems = ({ quote }: { quote: Quote }) => {
  const subtotal = quote.items.find(
    (item) => item.name === "Subtotal",
  ) as QuoteItem;
  const boardShipments =
    subtotal?.children.filter((item) => item.name.includes("BoardShipment")) ||
    [];
  const nonShipmentRows: EditableQuoteRow[] = (
    subtotal?.children.filter((item) => !item.name.includes("BoardShipment")) ||
    []
  ).map((item) => {
    return { item, parent: subtotal };
  });
  // Tariffs is a top-level sibling of Subtotal, so it has no parent
  const tariffRows: EditableQuoteRow[] = quote.items
    .filter((item) => item.name === "Tariffs")
    .map((item) => {
      return { item, parent: null };
    });

  return (
    <>
      {boardShipments.map((shipmentItem) => (
        <CustomQuoteTable
          key={shipmentItem.id}
          title={shipmentItem.name}
          rows={shipmentItem.children.map((item) => {
            return { item, parent: shipmentItem };
          })}
          currency={quote.currency}
          newItemParent={shipmentItem}
        />
      ))}
      <CustomQuoteTable
        title={"Order items"}
        rows={[...nonShipmentRows, ...tariffRows]}
        currency={quote.currency}
        newItemParent={subtotal}
      />
    </>
  );
};

export default function UpdateCustomQuote() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const board = useAppSelector(
    (state) => state.uploadedBoard.boards[boardId]?.board || null,
  ) as Board;
  const { updatingItem } = useUpdateQuoteItem();
  const { sendBoardEvent, loading } = useSendBoardEvent();

  const isProcessingQuote =
    useAppSelector(
      (state) => state.uploadedBoard.boards[boardId]?.isProcessing || false,
    ) && board.quote.state != QuoteState.ORDER_RECEIVED;

  const quoteIsUpdating =
    board.quote.state == QuoteState.UPDATING_QUOTE ||
    isProcessingQuote ||
    loading;

  const [expiresOn, setExpiresOn] = useState(
    todayInPhillyToDateInputString(DEFAULT_EXPIRATION),
  );
  const flashPCBSettings = useFlashPCBSettings() as FlashPCBSettings;
  const customConfirm = useCustomConfirm();

  const onChangeDate = (event: SyntheticEvent<HTMLInputElement>) => {
    const dateString = (event.target as HTMLInputElement).value;
    setExpiresOn(dateString);
  };

  const setQuote = () => {
    customConfirm("Set quote and send email to the customer?", () => {
      void sendBoardEvent(board.id, {
        machine_group: MachineGroup.BOARD,
        model_id: boardId,
        event: BoardTransitionEvent.SET_CUSTOM_QUOTE,
        update: {
          expires_on: expiresOn,
          currency: board.quote.currency,
        },
      });
    });
  };

  if (board.quote.items.length === 0) {
    return null;
  }

  if (board.quote.items.length > 0) {
    return (
      <QuoteToolContainer>
        <QuoteToolTitle>Set custom quote</QuoteToolTitle>
        <EditableLineItems quote={board.quote} />
        <LineItem
          leftText={"Subtotal"}
          rightText={formatCurrency(
            board.quote.items.find((item) => item.name === "Subtotal")
              ?.amount || 0,
            board.quote.currency,
          )}
          isUpdating={updatingItem || quoteIsUpdating}
        />
        <SetQuoteContainer>
          <DateInputContainer>
            <InputLabel>
              Quote expiration date (will expire{" "}
              {twentyFourHourToTwelveHour(flashPCBSettings.local_lead_time_eod)}{" "}
              of the day selected):
            </InputLabel>
            <Input
              style={{ color: COLORS.secondary }}
              type="date"
              value={expiresOn}
              onChange={onChangeDate}
              min={todayInPhillyToDateInputString()}
            />
          </DateInputContainer>
          <ButtonContainer>
            <Button
              label={"Set quote"}
              onClick={setQuote}
              disabled={quoteIsUpdating || updatingItem}
              isLoading={loading}
            />
          </ButtonContainer>
          <QuoteBreakdown />
        </SetQuoteContainer>
      </QuoteToolContainer>
    );
  }
}
